import React from 'react';
import { AlertTriangle, Info, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';

export type ConfirmationType = 'warning' | 'danger' | 'info' | 'success';

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  type?: ConfirmationType;
  isLoading?: boolean;
  /** 额外的内容，显示在消息下方 */
  children?: React.ReactNode;
}

const typeConfig: Record<ConfirmationType, {
  icon: React.ReactNode;
  iconBg: string;
  confirmBg: string;
  confirmHover: string;
}> = {
  warning: {
    icon: <AlertTriangle size={24} className="text-amber-600" />,
    iconBg: 'bg-amber-100',
    confirmBg: 'bg-amber-500',
    confirmHover: 'hover:bg-amber-600'
  },
  danger: {
    icon: <XCircle size={24} className="text-red-600" />,
    iconBg: 'bg-red-100',
    confirmBg: 'bg-red-500',
    confirmHover: 'hover:bg-red-600'
  },
  info: {
    icon: <Info size={24} className="text-sky-600" />,
    iconBg: 'bg-sky-100',
    confirmBg: 'bg-sky-500',
    confirmHover: 'hover:bg-sky-600'
  },
  success: {
    icon: <CheckCircle size={24} className="text-green-600" />,
    iconBg: 'bg-green-100',
    confirmBg: 'bg-green-500',
    confirmHover: 'hover:bg-green-600'
  }
};

/**
 * 通用确认弹窗组件
 * 
 * 使用示例:
 * ```tsx
 * <ConfirmModal
 *   isOpen={showConfirm}
 *   onClose={() => setShowConfirm(false)}
 *   onConfirm={handleDelete}
 *   title="确认删除"
 *   message="确定要删除这个文件吗？此操作无法撤销。"
 *   type="danger"
 *   confirmText="删除"
 * />
 * ```
 */
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = '确认',
  cancelText = '取消',
  type = 'warning',
  isLoading = false,
  children
}) => {
  const config = typeConfig[type];

  const handleConfirm = async () => {
    await onConfirm();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
        {/* 头部 */}
        <div className="p-6 pb-0">
          <div className="flex items-start gap-4">
            {/* 图标 */}
            <div className={cn('flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center', config.iconBg)}>
              {config.icon}
            </div>
            
            {/* 标题和消息 */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-slate-800">
                {title}
              </h3>
              <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                {typeof message === 'string' ? (
                  <p className="whitespace-pre-line">{message}</p>
                ) : (
                  message
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 额外内容 */}
        {children && (
          <div className="px-6 pt-4">
            {children}
          </div>
        )}

        {/* 按钮区域 */}
        <div className="p-6 pt-6 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
              config.confirmBg,
              config.confirmHover
            )}
          >
            {isLoading && <Loader2 size={16} className="animate-spin" />}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
